import React, { useCallback } from 'react';
import * as Inovx from '@shared';
import {
    KnowledgeLibraryBookCardModel,
    KnowledgeLibraryBreadcrumb,
    KnowledgeLibraryBrowseModel,
    KnowledgeLibraryTaxonomyCardModel,
} from './knowledgeLibraryBrowseModel';
import {
    KNOWLEDGE_LIBRARY_CARD_ZOOM_DEFAULT_PERCENT,
    KNOWLEDGE_LIBRARY_CARD_ZOOM_MAX_PERCENT,
    KNOWLEDGE_LIBRARY_CARD_ZOOM_MIN_PERCENT,
    KNOWLEDGE_LIBRARY_CARD_ZOOM_STEP_PERCENT,
    KNOWLEDGE_LIBRARY_KB_CARD_ZOOM_SESSION_KEY,
    loadKnowledgeLibraryCardZoomPercent,
    saveKnowledgeLibraryCardZoomPercent,
} from './knowledgeLibraryCardZoomSession';

interface KnowledgeLibraryCardsProps {
    browseModel: KnowledgeLibraryBrowseModel;
    detailKnowledgeBookId?: string;
    detailKnowledgeViews?: IKnowledgeView[];
    isLoadingDetailKnowledgeViews?: boolean;
    favoriteKnowledgeBookIds: string[];
    selectedKnowledgeBookIdSet: ReadonlySet<string>;
    selectionToolbar?: React.ReactNode;
    onSelectTag: (tagId: string) => void;
    onBreadcrumbClick: (breadcrumb: KnowledgeLibraryBreadcrumb) => void;
    onOpenKnowledgeBook: (kb: IKnowledgeBook) => void | Promise<void>;
    onToggleFavorite: (kb: IKnowledgeBook) => void | Promise<void>;
    onToggleKnowledgeBookSelection: (knowledgeBookId: string) => void;
    revealedKnowledgeBookId?: string | null;
    onRevealedKnowledgeBookIdChange?: (knowledgeBookId: string | null) => void;
    onRevealedKnowledgeBookChange?: (kb: IKnowledgeBook | null) => void;
    onBeginEdit: (args: any) => void;
    canModifyKnowledgeBook: (knowledgeBook: IKnowledgeBook) => boolean;
    canManageKnowledgeBook: (knowledgeBook: IKnowledgeBook) => boolean;
}

const KNOWLEDGE_BOOK_PLACEHOLDER_IMAGE = '/images/KB/KBLarge.png';
const KNOWLEDGE_LIBRARY_TAXONOMY_PLACEHOLDER_IMAGE = '/images/KB/BookShelf.png';
const DateTimeFormatHelper = Inovx.Shared.Common.DateTimeFormatHelper;
// Resolved per render: module-scope translation runs before InovxLoader.initialize()
// completes and would freeze (or blank out) the value for the whole session.
const getHistoryLoadingPlaceholder = () => InovxLoader.t('common:message.loading');

interface KnowledgeLibraryHistorySource {
    ModifiedByUser?: string;
    ModifiedDate?: string;
}

function getCardCountText(count: number): string {
    return count === 1
        ? InovxLoader.t('facility:knowledgeBook.oneMatchingKnowledgeBook')
        : InovxLoader.t('facility:knowledgeBook.matchingKnowledgeBooks', { count });
}

const KnowledgeLibraryCards: React.FC<KnowledgeLibraryCardsProps> = ({
    browseModel,
    detailKnowledgeBookId,
    detailKnowledgeViews = [],
    isLoadingDetailKnowledgeViews = false,
    favoriteKnowledgeBookIds,
    selectedKnowledgeBookIdSet,
    selectionToolbar,
    onSelectTag,
    onBreadcrumbClick,
    onOpenKnowledgeBook,
    onToggleFavorite,
    onToggleKnowledgeBookSelection,
    revealedKnowledgeBookId,
    onRevealedKnowledgeBookIdChange,
    onRevealedKnowledgeBookChange,
    onBeginEdit,
    canModifyKnowledgeBook,
    canManageKnowledgeBook
}) => {
    const [internalRevealedCardId, setInternalRevealedCardId] = React.useState<string | null>(null);
    const [cardZoomPercent, setCardZoomPercent] = React.useState<number>(() => loadKnowledgeLibraryCardZoomPercent(KNOWLEDGE_LIBRARY_KB_CARD_ZOOM_SESSION_KEY));
    const favoriteKnowledgeBookIdSet = React.useMemo(() => new Set(favoriteKnowledgeBookIds), [favoriteKnowledgeBookIds]);
    const hasCards = browseModel.taxonomyCards.length > 0 || browseModel.knowledgeBookCards.length > 0;
    const cardGridClassName = browseModel.taxonomyCards.length + browseModel.knowledgeBookCards.length < 5
        ? 'knowledge-library-card-grid knowledge-library-card-grid-compact'
        : 'knowledge-library-card-grid';
    const cardScale = cardZoomPercent / 100;
    const descriptionLineClamp = Math.max(2, Math.round(3 * cardScale));

    const revealedCardId = revealedKnowledgeBookId !== undefined ? revealedKnowledgeBookId : internalRevealedCardId;

    const setRevealedCardId = React.useCallback((cardId: string | null) => {
        setInternalRevealedCardId(cardId);
        onRevealedKnowledgeBookIdChange?.(cardId);
    }, [onRevealedKnowledgeBookIdChange]);

    React.useEffect(() => {
        if (!revealedKnowledgeBookId) {
            setRevealedCardId(null);
        }
    }, [browseModel.breadcrumbs, revealedKnowledgeBookId, setRevealedCardId]);

    React.useEffect(() => {
        const visibleKnowledgeBookIds = new Set(browseModel.knowledgeBookCards.map(card => card.id));

        setInternalRevealedCardId(previous => {
            if (!previous || visibleKnowledgeBookIds.has(previous)) {
                return previous;
            }

            return null;
        });
        if (revealedCardId && revealedKnowledgeBookId === undefined && !visibleKnowledgeBookIds.has(revealedCardId)) {
            setRevealedCardId(null);
        }
    }, [browseModel.knowledgeBookCards, revealedCardId, revealedKnowledgeBookId, setRevealedCardId]);

    React.useEffect(() => {
        if (!onRevealedKnowledgeBookChange) {
            return;
        }

        if (!revealedCardId) {
            onRevealedKnowledgeBookChange(null);
            return;
        }

        const revealedCard = browseModel.knowledgeBookCards.find(card => card.id === revealedCardId);
        onRevealedKnowledgeBookChange(revealedCard ? revealedCard.knowledgeBook : null);
    }, [revealedCardId, browseModel.knowledgeBookCards, onRevealedKnowledgeBookChange]);

    const showReveal = React.useCallback((cardId: string, event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setRevealedCardId(cardId);
    }, []);

    const hideReveal = React.useCallback((cardId: string, event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setRevealedCardId(revealedCardId === cardId ? null : revealedCardId);
    }, [revealedCardId, setRevealedCardId]);

    const handleBreadcrumbClick = React.useCallback((breadcrumb: KnowledgeLibraryBreadcrumb) => {
        onBreadcrumbClick(breadcrumb);
    }, [onBreadcrumbClick]);

    const handleZoomChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setCardZoomPercent(saveKnowledgeLibraryCardZoomPercent(KNOWLEDGE_LIBRARY_KB_CARD_ZOOM_SESSION_KEY, Number(event.target.value)));
    }, []);

    const handleZoomStep = React.useCallback((delta: number) => {
        setCardZoomPercent(previous => saveKnowledgeLibraryCardZoomPercent(KNOWLEDGE_LIBRARY_KB_CARD_ZOOM_SESSION_KEY, previous + delta));
    }, []);

    const handleZoomReset = React.useCallback(() => {
        setCardZoomPercent(saveKnowledgeLibraryCardZoomPercent(KNOWLEDGE_LIBRARY_KB_CARD_ZOOM_SESSION_KEY, KNOWLEDGE_LIBRARY_CARD_ZOOM_DEFAULT_PERCENT));
    }, []);

    const handleThumbnailKeyDown = React.useCallback((cardId: string, event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        setRevealedCardId(cardId);
    }, []);

    const toggleCardSelection = React.useCallback((cardId: string, event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        onToggleKnowledgeBookSelection(cardId);
    }, [onToggleKnowledgeBookSelection]);

    const handleEditClick = useCallback((rowData: IKnowledgeBook) => {
        const args = {
            requestType: 'beginEdit',
            rowData: rowData
        };
        onBeginEdit(args);
    }, [onBeginEdit]);

    const handleDeleteClick = useCallback((rowData: IKnowledgeBook) => {
        const args = {
            requestType: 'delete',
            data: [rowData]        };
        onBeginEdit(args);
    }, [onBeginEdit]);

    const getLatestHistory = React.useCallback((knowledgeBook: IKnowledgeBook) => {
        const historySources: KnowledgeLibraryHistorySource[] = [knowledgeBook];

        if (detailKnowledgeBookId === knowledgeBook.ID) {
            historySources.push(...detailKnowledgeViews);
        }

        return historySources.reduce<KnowledgeLibraryHistorySource | null>((latest, source) => {
            if (!source.ModifiedDate) {
                return latest;
            }

            if (!latest?.ModifiedDate || Date.parse(source.ModifiedDate) > Date.parse(latest.ModifiedDate)) {
                return source;
            }

            return latest;
        }, null);
    }, [detailKnowledgeBookId, detailKnowledgeViews]);

    const formatModifiedDate = React.useCallback((modifiedDate?: string) => {
        if (!modifiedDate) {
            return '';
        }

        return `${DateTimeFormatHelper.getFormatDate(modifiedDate)} ${DateTimeFormatHelper.getFormatTime(modifiedDate)}`;
    }, []);

    const renderTaxonomyCard = React.useCallback((card: KnowledgeLibraryTaxonomyCardModel) => {
        const handleClick = () => {
            onSelectTag(card.id);
        };

        return (
            <div className="knowledge-library-card-layout" key={card.id}>
                <button
                    type="button"
                    className="e-card knowledge-library-taxonomy-card"
                    onClick={handleClick}
                    title={card.name}
                >
                    <div className="knowledge-library-taxonomy-visual" aria-hidden="true">
                        <span className="book-self"></span>
                    </div>
                    <div className="knowledge-library-taxonomy-footer">
                        <div className="knowledge-library-taxonomy-title">{card.name}</div>
                        <div className="knowledge-library-taxonomy-count">{getCardCountText(card.knowledgeBookCount)}</div>
                    </div>
                </button>
            </div>
        );
    }, [onSelectTag]);

    const renderKnowledgeBookCard = React.useCallback((card: KnowledgeLibraryBookCardModel) => {
        const isRevealed = revealedCardId === card.id;
        const isSelected = selectedKnowledgeBookIdSet.has(card.id);
        const isFavorite = favoriteKnowledgeBookIdSet.has(card.id);
        const canModifyBook = canModifyKnowledgeBook(card.knowledgeBook);
        const canManageBook = canManageKnowledgeBook(card.knowledgeBook);
        const description = card.description || InovxLoader.t('facility:knowledgeBook.noDescription');
        const cardClassName = isRevealed ? 'e-card knowledge-library-kb-card is-revealed' : 'e-card knowledge-library-kb-card';
        const thumbnailUrl = card.knowledgeBook.ThumbnailImageUrl || KNOWLEDGE_BOOK_PLACEHOLDER_IMAGE;
        const isPlaceholderThumbnail = thumbnailUrl === KNOWLEDGE_BOOK_PLACEHOLDER_IMAGE;
        const latestHistory = getLatestHistory(card.knowledgeBook);
        const lastModifiedBy = latestHistory?.ModifiedByUser || '';
        const lastModifiedOn = formatModifiedDate(latestHistory?.ModifiedDate);
        const isLoadingHistory = detailKnowledgeBookId === card.knowledgeBook.ID && isLoadingDetailKnowledgeViews;
        const lastModifiedByText = isLoadingHistory ? getHistoryLoadingPlaceholder() : lastModifiedBy;
        const lastModifiedOnText = isLoadingHistory ? getHistoryLoadingPlaceholder() : lastModifiedOn;

        return (
            <div className="knowledge-library-card-layout" key={card.id}>
                <div className={cardClassName} title={card.name}>
                    <div
                        className={isRevealed ? 'knowledge-library-card-face is-revealed' : 'knowledge-library-card-face'}
                        role="button"
                        tabIndex={0}
                        aria-label={InovxLoader.t('facility:knowledgeBook.revealDetails')}
                        onClick={(event) => showReveal(card.id, event)}
                        onKeyDown={(event) => handleThumbnailKeyDown(card.id, event)}
                    >
                        <div className="knowledge-library-card-placeholder">
                            <button
                                type="button"
                                className={isSelected
                                    ? 'knowledge-library-card-selection-toggle is-selected'
                                    : 'knowledge-library-card-selection-toggle'}
                                title={isSelected ? InovxLoader.t('facility:knowledgeBook.deselect') : InovxLoader.t('facility:knowledgeBook.select')}
                                aria-label={isSelected ? InovxLoader.t('facility:knowledgeBook.deselect') : InovxLoader.t('facility:knowledgeBook.select')}
                                onClick={(event) => toggleCardSelection(card.id, event)}
                            >
                                <span className="fa fa-check" aria-hidden="true"></span>
                            </button>
                            <img
                                src={thumbnailUrl}
                                alt=""
                                className={isPlaceholderThumbnail ? 'knowledge-library-card-placeholder-image' : undefined}
                            />
                        </div>
                        <div className="knowledge-library-card-name" title={card.name}>{card.name}</div>
                        <div className="knowledge-library-card-actions-row">
                            <div className="e-card-actions knowledge-library-card-primary-actions">
                                <button
                                    type="button"
                                    className="e-btn e-outline e-primary knowledge-library-card-open-button"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        onOpenKnowledgeBook(card.knowledgeBook);
                                    }}
                                >
                                    {InovxLoader.t('facility:knowledgeBook.open')}
                                </button>
                                <div className="knowledge-library-card-secondary-actions">
                                    <button
                                        type="button"
                                        className="e-card-btn knowledge-library-card-favorite knowledge-library-card-preview"
                                        title={InovxLoader.t('facility:knowledgeBook.preview')}
                                        aria-label={InovxLoader.t('facility:knowledgeBook.preview')}
                                        onClick={(event) => showReveal(card.id, event)}
                                    >
                                        <img src="/images/KB/Preview.png" alt="" aria-hidden="true" />
                                    </button>
                                    <button
                                        type="button"
                                        className={isFavorite ? 'e-card-btn knowledge-library-card-favorite active' : 'e-card-btn knowledge-library-card-favorite'}
                                        title={isFavorite ? InovxLoader.t('facility:knowledgeBook.removeFavorite') : InovxLoader.t('facility:knowledgeBook.addFavorite')}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            onToggleFavorite(card.knowledgeBook);
                                        }}
                                    >
                                        <span className={`fa ${isFavorite ? 'fa-star' : 'fa-star-o'}`} aria-hidden="true"></span>
                                    </button>
                                    {(canModifyBook || canManageBook) && (<button
                                        type="button"
                                        className={'e-card-btn knowledge-library-card-favorite'}
                                        title={InovxLoader.t('facility:preferences.editButton')}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            handleEditClick(card.knowledgeBook);
                                        }}
                                    >
                                        <span className={`fa fa-pencil`} aria-hidden="true"></span>
                                    </button>)}
                                    {canManageBook && (<button
                                        type="button"
                                        className={'e-card-btn knowledge-library-card-favorite'}
                                        title={InovxLoader.t('facility:knowledgeBook.delete')}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            handleDeleteClick(card.knowledgeBook);
                                        }}
                                    >
                                        <span className={`fa fa-trash-o`} aria-hidden="true"></span>
                                    </button>)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={isRevealed ? 'knowledge-library-card-reveal-face is-revealed' : 'knowledge-library-card-reveal-face'}>
                            <div className="knowledge-library-card-reveal-header-shell">
                                <div className="knowledge-library-card-reveal-caption">
                                    <span className="kb-icon kb-book-icon" aria-hidden="true"></span>
                                    <div className="knowledge-library-card-reveal-title">{card.name}</div>
                                </div>
                                <button
                                    type="button"
                                    className="knowledge-library-card-collapse"
                                    title={InovxLoader.t('facility:knowledgeBook.closeDetails')}
                                    onClick={(event) => hideReveal(card.id, event)}
                                >
                                    <span className="e-icons e-close" aria-hidden="true"></span>
                                </button>
                            </div>
                            <div className="knowledge-library-card-reveal-content">
                                <div className="knowledge-library-card-reveal-text knowledge-library-card-reveal-description">{description}</div>
                                <div className="knowledge-library-card-reveal-section">
                                    <div className="knowledge-library-card-reveal-label">{InovxLoader.t('facility:knowledgeBook.lastModifiedBy')}</div>
                                    <div className="knowledge-library-card-reveal-text">{lastModifiedByText}</div>
                                </div>
                                <div className="knowledge-library-card-reveal-section">
                                    <div className="knowledge-library-card-reveal-label">{InovxLoader.t('facility:knowledgeBook.lastModifiedOn')}</div>
                                    <div className="knowledge-library-card-reveal-text">{lastModifiedOnText}</div>
                                </div>
                                <div className="knowledge-library-tag-chip-list">
                                    {card.classificationTags.map(tag => (
                                        <span className="knowledge-library-tag-chip" key={tag.ID}>{tag.Name}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                </div>
            </div>
        );
    }, [canManageKnowledgeBook, canModifyKnowledgeBook, detailKnowledgeBookId, favoriteKnowledgeBookIdSet, formatModifiedDate, getLatestHistory, handleThumbnailKeyDown, hideReveal, isLoadingDetailKnowledgeViews, onOpenKnowledgeBook, onToggleFavorite, revealedCardId, selectedKnowledgeBookIdSet, showReveal, toggleCardSelection]);

    return (
        <div className="knowledge-library-cards-shell">
            <div className="knowledge-library-breadcrumbs-row">
                <div className="knowledge-library-breadcrumbs">
                    <Inovx.Shared.Controls.BreadcrumbPanel
                        breadcrumb={browseModel.breadcrumbs}
                        onClick={handleBreadcrumbClick}
                        displayProp="label"
                    />
                </div>
                {selectionToolbar}                
                <div className="knowledge-library-card-zoom" title={InovxLoader.t('facility:knowledgeBook.cardZoom')}>
                    <button
                        type="button"
                        className="knowledge-library-card-zoom-button"
                        title={InovxLoader.t('facility:knowledgeBook.cardZoomOut')}
                        aria-label={InovxLoader.t('facility:knowledgeBook.cardZoomOut')}
                        onClick={() => handleZoomStep(-KNOWLEDGE_LIBRARY_CARD_ZOOM_STEP_PERCENT)}
                        disabled={cardZoomPercent <= KNOWLEDGE_LIBRARY_CARD_ZOOM_MIN_PERCENT}
                    >
                        <span className="fa fa-search-minus" aria-hidden="true"></span>
                    </button>
                    <input
                        type="range"
                        className="knowledge-library-card-zoom-slider"
                        min={KNOWLEDGE_LIBRARY_CARD_ZOOM_MIN_PERCENT}
                        max={KNOWLEDGE_LIBRARY_CARD_ZOOM_MAX_PERCENT}
                        step={KNOWLEDGE_LIBRARY_CARD_ZOOM_STEP_PERCENT}
                        value={cardZoomPercent}
                        onChange={handleZoomChange}
                        aria-label={InovxLoader.t('facility:knowledgeBook.cardZoom')}
                    />
                    <button
                        type="button"
                        className="knowledge-library-card-zoom-button"
                        title={InovxLoader.t('facility:knowledgeBook.cardZoomIn')}
                        aria-label={InovxLoader.t('facility:knowledgeBook.cardZoomIn')}
                        onClick={() => handleZoomStep(KNOWLEDGE_LIBRARY_CARD_ZOOM_STEP_PERCENT)}
                        disabled={cardZoomPercent >= KNOWLEDGE_LIBRARY_CARD_ZOOM_MAX_PERCENT}
                    >
                        <span className="fa fa-search-plus" aria-hidden="true"></span>
                    </button>
                    <button
                        type="button"
                        className="knowledge-library-card-zoom-value"
                        title={InovxLoader.t('facility:knowledgeBook.cardZoomReset')}
                        aria-label={InovxLoader.t('facility:knowledgeBook.cardZoomReset')}
                        onClick={handleZoomReset}
                    >
                        {`${cardZoomPercent}%`}
                    </button>
                </div>
            </div>

            {!hasCards ? (
                <div className="knowledge-library-empty-state">{browseModel.emptyMessage}</div>
            ) : (
                <div
                    className={cardGridClassName}
                    style={{
                        ['--klib-card-scale' as string]: cardScale,
                        ['--klib-card-description-line-clamp' as string]: descriptionLineClamp
                    }}
                >
                    {browseModel.taxonomyCards.map(renderTaxonomyCard)}
                    {browseModel.knowledgeBookCards.map(renderKnowledgeBookCard)}
                </div>
            )}
        </div>
    );
};

export default KnowledgeLibraryCards;
